/**
 * A FAILED LISTENER COMES BACK WHEN THE PERSON DOES.
 *
 * A Firestore onSnapshot error is terminal: the listener is dead and nothing
 * resubscribes it. The must-read, mention and staff announcement listeners
 * behind the home card, the bell and the phone app badge used to stay dead
 * for the rest of the session after one bad moment (a phone waking up in a
 * dead corner of the building), and the app badge holds its last number
 * while a check has failed (AppBadgeSync), so that hold needs a way to end.
 *
 * `useReviveAfterError(failed)` returns a number to put in the listener
 * effect's deps. While `failed` is true it watches for the page becoming
 * visible again and for the network coming back, and bumps the number so the
 * effect rebuilds its listener. BOUNDED on purpose:
 *   - nothing is watched, and nothing is ever rebuilt, while the listener is
 *     healthy, so a healthy session pays nothing for this;
 *   - one rebuild per return to the app or reconnect, never a timer loop;
 *   - at most one rebuild every REVIVE_MIN_GAP_MS per hook, because
 *     'online' and 'visibilitychange' commonly fire together when a phone
 *     wakes, and one rebuild answers both.
 *
 * lib/readEpoch.ts is the app-wide version of the same idea, bumped only by
 * a Try again control on screen; that one rebuilds EVERY wired listener, and
 * these three hooks are mounted by the always-on bell and badge, so they
 * revive themselves and nothing else.
 */
import { useEffect, useRef, useState } from 'react';

export const REVIVE_MIN_GAP_MS = 3000;

/** The throttle, alone, so it can be pinned without a clock or React. */
export function shouldRevive(lastRevivedAt: number | null, now: number): boolean {
  return lastRevivedAt === null || now - lastRevivedAt >= REVIVE_MIN_GAP_MS;
}

export function useReviveAfterError(failed: boolean): number {
  const [token, setToken] = useState(0);
  const lastRevivedAt = useRef<number | null>(null);
  useEffect(() => {
    if (!failed) return undefined;
    if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;
    const revive = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (!shouldRevive(lastRevivedAt.current, now)) return;
      lastRevivedAt.current = now;
      setToken((n) => n + 1);
    };
    document.addEventListener('visibilitychange', revive);
    window.addEventListener('online', revive);
    return () => {
      document.removeEventListener('visibilitychange', revive);
      window.removeEventListener('online', revive);
    };
  }, [failed]);
  return token;
}
